// Import statements related to date handling
import {
  isBefore,
  isAfter,
  isEqual,
  startOfToday,
  startOfTomorrow,
  startOfYesterday,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  parse,
  addDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  addQuarters,
  subQuarters,
  addYears,
  subYears,
} from "date-fns";
import { enUS } from "date-fns/locale";

import { isRange, extractWords } from "./util";

const NumberConditions = {
  Equals: "Equals",
  DoesNotEqual: "Does Not Equal",
  GreaterThan: "Greater Than",
  GreaterThanOrEqual: "Greater Than or Equal To",
  LessThan: "Less Than",
  LessThanOrEqual: "Less Than or Equal To",
};

const TextConditions = {
  Equals: "Equals",
  DoesNotEqual: "Does Not Equal",
  BeginsWith: "Begins With",
  DoesNotBeginWith: "Does Not Begin With",
  EndsWith: "Ends With",
  DoesNotEndWith: "Does Not End With",
  Contains: "Contains",
  DoesNotContain: "Does Not Contain",
};

const DateConditions = {
  Equals: "Equals",
  Before: "Before",
  After: "After",
  Tomorrow: "Tomorrow",
  Today: "Today",
  Yesterday: "Yesterday",
  NextWeek: "Next Week",
  ThisWeek: "This Week",
  LastWeek: "Last Week",
  NextMonth: "Next Month",
  ThisMonth: "This Month",
  LastMonth: "Last Month",
  NextQuarter: "Next Quarter",
  ThisQuarter: "This Quarter",
  LastQuarter: "Last Quarter",
  NextYear: "Next Year",
  ThisYear: "This Year",
  LastYear: "Last Year",
  YearToDate: "Year to Date",
  AllDatesInThePeriod: "All Dates in the Period",
};

const TimeConditions = {
  Equals: "Equals",
  Before: "Before",
  After: "After",
  Morning: "Morning", // e.g., 6 AM to 12 PM
  Afternoon: "Afternoon", // e.g., 12 PM to 6 PM
  Evening: "Evening", // e.g., 6 PM to 9 PM
  Night: "Night", // e.g., 9 PM to 6 AM
};

export { NumberConditions, TextConditions, DateConditions, TimeConditions };

export const stylePatterns = [
  { isTable: true, 0: { bgColor: "#BCEBFF" }, 1: { bgColor: "#D1F1FF" } },
  { isTable: true, 0: { bgColor: "#FAE0FF" }, 1: { bgColor: "#F9EDFC" } },
  { isTable: true, 0: { bgColor: "#FEECB5" }, 1: { bgColor: "#FEF1C8" } },
  { isTable: true, 0: { bgColor: "#DFE2E1" }, 1: { bgColor: "#EAECEB" } },
];

// Utility functions related to regex for text filtering
export const getRegex = (condition, text) => {
  const escapedText = text.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");

  switch (condition) {
    case TextConditions.Equals:
      return new RegExp(`^${escapedText}$`);
    case TextConditions.DoesNotEqual:
      return new RegExp(`^(?!${escapedText}$).*$`);
    case TextConditions.BeginsWith:
      return new RegExp(`^${escapedText}`);
    case TextConditions.DoesNotBeginWith:
      return new RegExp(`^(?!${escapedText}).*`);
    case TextConditions.EndsWith:
      return new RegExp(`${escapedText}$`);
    case TextConditions.DoesNotEndWith:
      return new RegExp(`.*(?<!${escapedText})$`);
    case TextConditions.Contains:
      return new RegExp(`${escapedText}`);
    case TextConditions.DoesNotContain:
      return new RegExp(`^(?!.*${escapedText}).*$`);
    default:
      throw new Error("Invalid condition");
  }
};

export const combineRegex = (regex1, regex2, logicalOperator) => {
  switch (logicalOperator) {
    case "AND":
      return new RegExp(`(?=.*${regex1})(?=.*${regex2})`);
    case "OR":
      return new RegExp(`(${regex1})|(${regex2})`);
    default:
      throw new Error("Invalid logical operator");
  }
};

export const tableHeaderValidation = (input) => {
  const regex = /^[A-Za-z0-9_]+$/;
  return regex.test(input);
};

export const tableTitleValidation = (input) => {
  const regex = /^[A-Za-z][A-Za-z0-9_]*$/;
  return regex.test(input);
};

// Parsing and comparison functions for date and time
export const parseDate = (dateString) => {
  const formats = [
    "HH:mm",
    "HH:mm:ss",
    "hh:mm:ss a",
    "yyyy-MM-dd HH:mm:ss",
    "yyyy-MM-dd'T'HH:mm:ss",
    "MM/dd/yyyy hh:mm:ss a",
    "yyyy-MM-dd",
    "MM/dd/yyyy",
    "dd/MM/yyyy",
    "M/d/yyyy",
    "dd.MM.yyyy",
    "d.M.yyyy",
    "d MMM yyyy",
    "d MMMM yyyy",
  ];

  let parsedDate = null;
  if (!dateString) {
    return;
  }
  for (let format of formats) {
    try {
      parsedDate = parse(dateString, format, new Date(), { locale: enUS });
      // If the date is valid, break out of the loop
      if (!isNaN(parsedDate)) {
        break;
      }
    } catch (error) {
      // If parsing fails, continue to the next format
      continue;
    }
  }

  // Throw an error if none of the formats matched
  if (!parsedDate || isNaN(parsedDate)) {
    throw new Error("Invalid date format");
  }

  return parsedDate;
};

// data will be in array of object format  [{"rowId":"6","value":1}]
export const sortMatrix = (data, completeData, order = "asc") => {
  const compareValues = (a, b) => {
    const valA = a.value;
    const valB = b.value;

    // Handle null, undefined, or empty string values, but not zero
    if ((valA === null || valA === undefined || valA === "") && (valB === null || valB === undefined || valB === ""))
      return 0;
    if (valA === null || valA === undefined || valA === "") return 1;
    if (valB === null || valB === undefined || valB === "") return -1;

    // Compare values
    if (valA < valB) return order === "asc" ? -1 : 1;
    if (valA > valB) return order === "asc" ? 1 : -1;
    return 0;
  };

  // Sort the data based on the value
  const sortedData = [...data].sort(compareValues);

  // Create a map of data rowIds for quick lookup
  const dataRowIds = new Set(data.map((item) => item.rowId));

  // Create the result array with source and target row IDs from sortedData
  const result = sortedData.map((item, index) => ({
    targetRowId: item.rowId,
    sourceRowId: data[index].rowId,
  }));

  let tempResult = [];
  // Add any rows from completeData that are not in data
  let j = 0;
  completeData.forEach((item) => {
    if (!dataRowIds.has(item.rowId)) {
      tempResult.push({
        targetRowId: item.rowId,
        sourceRowId: item.rowId,
      });
    } else {
      tempResult.push(result[j++]);
    }
  });

  return tempResult;
};

// Functions to apply different types of filters
export const applyNumberFilter = (condition, values) => {
  let matchRowIds = [];
  let noMatchRowIds = [];

  // If selectedValues is not empty, use it directly
  if (condition.selectedValues && condition.selectedValues.length > 0) {
    values.forEach((valueObj) => {
      if (
        condition.selectedValues
          .map(String)
          .map((val) => val.toLowerCase())
          .includes(String(valueObj.value ?? "").toLowerCase())
      ) {
        matchRowIds.push(valueObj.rowId);
      } else {
        noMatchRowIds.push(valueObj.rowId);
      }
    });
    return { matchRowIds, noMatchRowIds };
  }

  values.forEach((valueObj) => {
    const value = parseFloat(valueObj.value);
    const value1 = parseFloat(condition.value1);
    const value2 = condition.value2 ? parseFloat(condition.value2) : null;

    let match = false;

    switch (condition.condition1) {
      case NumberConditions.Equals:
        match = value === value1;
        break;
      case NumberConditions.DoesNotEqual:
        match = value !== value1;
        break;
      case NumberConditions.GreaterThan:
        match = value > value1;
        break;
      case NumberConditions.GreaterThanOrEqual:
        match = value >= value1;
        break;
      case NumberConditions.LessThan:
        match = value < value1;
        break;
      case NumberConditions.LessThanOrEqual:
        match = value <= value1;
        break;
      default:
        throw new Error("Invalid condition");
    }

    if (condition.condition2 && condition.logicalOperator && value2 !== null) {
      let secondaryMatch = false;
      switch (condition.condition2) {
        case NumberConditions.Equals:
          secondaryMatch = value === value2;
          break;
        case NumberConditions.DoesNotEqual:
          secondaryMatch = value !== value2;
          break;
        case NumberConditions.GreaterThan:
          secondaryMatch = value > value2;
          break;
        case NumberConditions.GreaterThanOrEqual:
          secondaryMatch = value >= value2;
          break;
        case NumberConditions.LessThan:
          secondaryMatch = value < value2;
          break;
        case NumberConditions.LessThanOrEqual:
          secondaryMatch = value <= value2;
          break;
        default:
          throw new Error("Invalid condition");
      }

      switch (condition.logicalOperator) {
        case "AND":
          match = match && secondaryMatch;
          break;
        case "OR":
          match = match || secondaryMatch;
          break;
        default:
          throw new Error("Invalid logical operator");
      }
    }

    if (match) {
      matchRowIds.push(valueObj.rowId);
    } else {
      noMatchRowIds.push(valueObj.rowId);
    }
  });

  return { matchRowIds, noMatchRowIds };
};

export const applyTextFilter = (condition, values) => {
  let matchRowIds = [];
  let noMatchRowIds = [];

  // If selectedValues is not empty, use it directly
  if (condition.selectedValues && condition.selectedValues.length > 0) {
    values.forEach((valueObj) => {
      if (
        condition.selectedValues
          .map(String)
          .map((val) => val.toLowerCase())
          .includes(String(valueObj.value ?? "").toLowerCase())
      ) {
        matchRowIds.push(valueObj.rowId);
      } else {
        noMatchRowIds.push(valueObj.rowId);
      }
    });
    return { matchRowIds, noMatchRowIds };
  }

  let regex = getRegex(condition.condition1, condition.value1);
  if (condition.condition2 && condition.logicalOperator && condition.value2) {
    let regex2 = getRegex(condition.condition2, condition.value2);
    regex = combineRegex(regex, regex2, condition.logicalOperator);
  }
  values.forEach((valueObj) => {
    const value = valueObj.value;

    if (regex.test(value)) {
      matchRowIds.push(valueObj.rowId);
    } else {
      noMatchRowIds.push(valueObj.rowId);
    }
  });
  return { matchRowIds, noMatchRowIds };
};

export const applyDateFilter = async (condition, values) => {
  let matchRowIds = [];
  let noMatchRowIds = [];

  if (condition.selectedValues && condition.selectedValues.length > 0) {
    values.forEach((valueObj) => {
      if (
        condition.selectedValues
          .map(String)
          .map((val) => val.toLowerCase())
          .includes(String(valueObj.value ?? "").toLowerCase())
      ) {
        matchRowIds.push(valueObj.rowId);
      } else {
        noMatchRowIds.push(valueObj.rowId);
      }
    });
    return { matchRowIds, noMatchRowIds };
  }

  const value1 = condition.value1 ? parseDate(condition.value1) : null;
  const value2 = condition.value2 ? parseDate(condition.value2) : null;

  values.forEach((valueObj) => {
    const value = parseDate(valueObj.value);
    let match = false;

    switch (condition.condition1) {
      case DateConditions.Equals:
        match = isEqual(value, value1);
        break;
      case DateConditions.Before:
        match = isBefore(value, value1);
        break;
      case DateConditions.After:
        match = isAfter(value, value1);
        break;
      case DateConditions.Tomorrow:
        match = isEqual(value, startOfTomorrow());
        break;
      case DateConditions.Today:
        match = isEqual(value, startOfToday());
        break;
      case DateConditions.Yesterday:
        match = isEqual(value, startOfYesterday());
        break;
      case DateConditions.NextWeek: {
        const start = addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), 1);
        const end = addDays(start, 6);
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.ThisWeek: {
        const start = startOfWeek(new Date(), { weekStartsOn: 1 });
        const end = endOfWeek(new Date(), { weekStartsOn: 1 });
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.LastWeek: {
        const start = subWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), 1);
        const end = addDays(start, 6);
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.NextMonth: {
        const start = addMonths(startOfMonth(new Date()), 1);
        const end = endOfMonth(start);
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.ThisMonth: {
        const start = startOfMonth(new Date());
        const end = endOfMonth(new Date());
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.LastMonth: {
        const start = subMonths(startOfMonth(new Date()), 1);
        const end = endOfMonth(start);
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.NextQuarter: {
        const start = addQuarters(startOfQuarter(new Date()), 1);
        const end = endOfQuarter(start);
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.ThisQuarter: {
        const start = startOfQuarter(new Date());
        const end = endOfQuarter(new Date());
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.LastQuarter: {
        const start = subQuarters(startOfQuarter(new Date()), 1);
        const end = endOfQuarter(start);
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.NextYear: {
        const start = addYears(startOfYear(new Date()), 1);
        const end = addYears(endOfYear(new Date()), 1);
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.ThisYear: {
        const start = startOfYear(new Date());
        const end = endOfYear(new Date());
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.LastYear: {
        const start = subYears(startOfYear(new Date()), 1);
        const end = subYears(endOfYear(new Date()), 1);
        match = (isAfter(value, start) || isEqual(value, start)) && (isBefore(value, end) || isEqual(value, end));
        break;
      }
      case DateConditions.YearToDate: {
        const start = startOfYear(new Date());
        const now = startOfToday();
        match = (isAfter(value, start) || isEqual(value, start)) && isBefore(value, now);
        break;
      }
      case DateConditions.AllDatesInThePeriod:
        match = true;
        break;
      default:
        throw new Error("Invalid condition");
    }

    if (condition.condition2 && condition.logicalOperator && value2 !== null) {
      let secondaryMatch = false;
      switch (condition.condition2) {
        case DateConditions.Equals:
          secondaryMatch = isEqual(value, value2);
          break;
        case DateConditions.Before:
          secondaryMatch = isBefore(value, value2);
          break;
        case DateConditions.After:
          secondaryMatch = isAfter(value, value2);
          break;
        default:
          throw new Error("Invalid condition");
      }

      switch (condition.logicalOperator) {
        case "AND":
          match = match && secondaryMatch;
          break;
        case "OR":
          match = match || secondaryMatch;
          break;
        default:
          throw new Error("Invalid logical operator");
      }
    }

    if (match) {
      matchRowIds.push(valueObj.rowId);
    } else {
      noMatchRowIds.push(valueObj.rowId);
    }
  });

  return { matchRowIds, noMatchRowIds };
};

export const applyTimeFilter = (condition, values) => {
  let matchRowIds = [];
  let noMatchRowIds = [];

  // If selectedValues is not empty, use it directly
  if (condition.selectedValues && condition.selectedValues.length > 0) {
    values.forEach((valueObj) => {
      if (
        condition.selectedValues
          .map(String)
          .map((val) => val.toLowerCase())
          .includes(String(valueObj.value ?? "").toLowerCase())
      ) {
        matchRowIds.push(valueObj.rowId);
      } else {
        noMatchRowIds.push(valueObj.rowId);
      }
    });
    return { matchRowIds, noMatchRowIds };
  }

  values.forEach((valueObj) => {
    if (!valueObj.value) {
      // Handle empty or null value (e.g., skip, log, or add to noMatchRowIds)
      noMatchRowIds.push(valueObj.rowId); // Add to noMatchRowIds by default
      return; // Skip processing this item
    }
    const value = parseDate(valueObj.value);

    const parsedTime1 = parseDate(condition.value1);
    const parsedTime2 = condition.value2 ? parseDate(condition.value2) : null;

    let match = false;

    switch (condition.condition1) {
      case TimeConditions.Equals:
        match = isEqual(value, parsedTime1);
        break;
      case TimeConditions.Before:
        match = isBefore(value, parsedTime1);
        break;
      case TimeConditions.After:
        match = isAfter(value, parsedTime1);
        break;
      case TimeConditions.Morning:
        match = value.getHours() >= 6 && value.getHours() < 12;
        break;
      case TimeConditions.Afternoon:
        match = value.getHours() >= 12 && value.getHours() < 18;
        break;
      case TimeConditions.Evening:
        match = value.getHours() >= 18 && value.getHours() < 21;
        break;
      case TimeConditions.Night:
        match = value.getHours() >= 21 || value.getHours() < 6;
        break;
      default:
        throw new Error("Invalid condition");
    }

    if (condition.condition2 && condition.logicalOperator && parsedTime2 !== null) {
      let secondaryMatch = false;
      switch (condition.condition2) {
        case TimeConditions.Equals:
          secondaryMatch = isEqual(value, parsedTime2);
          break;
        case TimeConditions.Before:
          secondaryMatch = isBefore(value, parsedTime2);
          break;
        case TimeConditions.After:
          secondaryMatch = isAfter(value, parsedTime2);
          break;
        default:
          throw new Error("Invalid condition");
      }

      switch (condition.logicalOperator) {
        case "AND":
          match = match && secondaryMatch;
          break;
        case "OR":
          match = match || secondaryMatch;
          break;
        default:
          throw new Error("Invalid logical operator");
      }
    }

    if (match) {
      matchRowIds.push(valueObj.rowId);
    } else {
      noMatchRowIds.push(valueObj.rowId);
    }
  });

  return { matchRowIds, noMatchRowIds };
};

// Table Ranges Encoding and Decoding
export const needsEncoding = (input) => {
  const tableFieldPattern = /\w+(?:\.\.?\w+)+/;
  return tableFieldPattern.test(input);
};
export const needsDecoding = (input, matchArray = []) => {
  // First check the original encoded pattern
  const encodedPattern =
    /\w+(?:\[(@?)\$?#?(\w+)\]|\[(@?\$?)\[#?\w+(?:\]:\[#?\w+)*\]\]|\[\[(@?\$?)(#?\w+)\],\[(@?\$?)(#?\w+(?:\]:\[#?\w+)*)\]\]|\[\[#?\w+\],\$?\[#?\w+(?:\]:\[#?\w+)*\]\])/;

  // If no matchArray provided or it's empty, just return the original pattern test
  if (!matchArray || matchArray.length === 0) {
    return encodedPattern.test(input);
  }

  // Extract actual words from the input
  const inputWords = extractWords(input);

  // Check if any string from matchArray exactly matches one of the extracted words
  const hasMatch = matchArray.some((str) => {
    if (typeof str !== "string") return false;
    return inputWords.includes(str.toLowerCase());
  });
  // Return true if either condition is met
  return encodedPattern.test(input) || hasMatch;
};
export const encodeTableRefrencing = (input, absoluteRanges = []) => {
  // Track occurrences of each full reference within input
  const referenceCounts = {};

  return (input + "").replace(/(\w+)((?:\.\.?\w+)+)/g, (match, table, fields) => {
    const fieldParts = fields.match(/(?:\.\.?\w+)/g);
    const fullReference = table + fields;

    // Initialize or increment occurrence count for the current full reference
    referenceCounts[fullReference] = (referenceCounts[fullReference] || 0) + 1;

    // Check if current occurrence of fullReference should be absolute
    const isAbsolute = absoluteRanges.some((range) => {
      return range.reference === fullReference && range.index === referenceCounts[fullReference] - 1;
    });

    // Encode each field part
    const encodedFields = fieldParts.map((field) => {
      if (field.startsWith("..")) {
        return `#${field.slice(2)}`;
      }
      return field.slice(1);
    });

    // Construct the encoded output based on absolute or relative
    if (encodedFields.length === 1) {
      if (encodedFields[0].startsWith("#")) {
        return `${table}[${encodedFields[0]}]`; // No $ for header and total references
      }
      return `${table}[${isAbsolute ? "$" : ""}${encodedFields[0]}]`;
    } else {
      // Separate header field if present
      const headerIndex = encodedFields.findIndex((f) => f.startsWith("#"));
      if (headerIndex !== -1) {
        const header = encodedFields.splice(headerIndex, 1)[0];
        const columnsJoined = encodedFields.join("]:[");
        return `${table}[[${header}],${isAbsolute ? "$" : ""}[${columnsJoined}]]`;
      } else {
        const columnsJoined = encodedFields.join("]:[");
        return `${table}[${isAbsolute ? "$" : ""}[${columnsJoined}]]`;
      }
    }
  });
};

export const decodeTableRefrencingWithTypes = (input) => {
  const result = [];

  (input + "").replace(
    /(\w+)(?:\[(@?)(\$?)#?(\w+)\]|\[(@?\$?)\[#?(\w+(?:\]:\[#?\w+)*)\]\]|\[\[(@?\$?)(#?\w+)\],\[(@?\$?)(#?\w+(?:\]:\[#?\w+)*)\]\]|\[\[(#?\w+)\],\$?\[(#?\w+(?:\]:\[#?\w+)*)\]\])/g,
    (
      match,
      table,
      atSymbol1,
      dollarSymbol1,
      singleField,
      atDollarSymbol1,
      multipleFields,
      atDollarSymbol2,
      headerField,
      atDollarSymbol3,
      restFields,
      newHeaderField,
      newRestFields
    ) => {
      let decodedString = "";
      let hasAtSymbol =
        atSymbol1 ||
        atDollarSymbol1?.includes("@") ||
        atDollarSymbol2?.includes("@") ||
        atDollarSymbol3?.includes("@") ||
        match.includes("@");
      let hasDollarSymbol =
        dollarSymbol1 ||
        atDollarSymbol1?.includes("$") ||
        atDollarSymbol2?.includes("$") ||
        atDollarSymbol3?.includes("$") ||
        match.includes("$");

      const processField = (field, prefix = "") => {
        if (field.startsWith("@")) {
          hasAtSymbol = true;
          field = field.slice(1);
        }
        if (field.startsWith("$")) {
          hasDollarSymbol = true;
          field = field.slice(1);
        }
        return field.startsWith("#") ? `${prefix}..${field.slice(1)}` : `${prefix}.${field}`;
      };

      if (singleField) {
        decodedString = match.includes("#") ? `${table}..${singleField}` : `${table}.${singleField}`;
      } else if (headerField && restFields) {
        const fields = [processField(headerField), ...restFields.split("]:[").map((f) => processField(f, ""))];
        decodedString = `${table}${fields.join("")}`;
      } else if (multipleFields) {
        const fields = multipleFields.split("]:[").map((f) => processField(f, ""));
        decodedString = `${table}${fields.join("")}`;
      } else if (newHeaderField && newRestFields) {
        const headerPart = processField(newHeaderField);
        const restPart = newRestFields
          .split("]:[")
          .map((f) => processField(f, ""))
          .join("");
        decodedString = `${table}${headerPart}${restPart}`;
        hasDollarSymbol = true; // These new cases are always absolute
      }

      if (decodedString) {
        result.push({
          original: match,
          decoded: decodedString.replace(/@/g, "").replace(/\$/g, ""),
          type:
            hasAtSymbol || match.includes("[#header],") || match.includes("[#total],") ? "inRowNameRange" : "nameRange",
          relativity: hasDollarSymbol ? "absolute" : "relative",
        });
      }
    }
  );

  return result;
};

export const decodeTableRefrencing = (title, cellId, rootGetters, rootState, inRowNameRangeFunction = (x) => x) => {
  const decodedReferences = decodeTableRefrencingWithTypes(title);
  let decodedTitle = title;
  let nameRanges = [];
  decodedReferences.forEach((ref) => {
    if (ref.type === "nameRange") {
      decodedTitle = decodedTitle.replace(ref.original, ref.decoded);
      nameRanges.push({
        decoded: ref.decoded,
        relativity: ref.relativity,
      });
    } else if (ref.type === "inRowNameRange") {
      const replacement = inRowNameRangeFunction(
        ref.decoded,
        cellId,
        rootGetters,
        rootState,
        ref.relativity === "absolute"
      );
      decodedTitle = decodedTitle.replace(ref.original, replacement || ref.original);
    }
  });
  return { decodedTitle, nameRanges };
};

export const extractNamedRanges = (input, tableNames = []) => {
  // Decode the input to ensure it handles both encoded and decoded formats
  const decodedInput = decodeTableRefrencingWithTypes(input);
  let namedRanges = [...decodedInput];
  // Check for standalone table names and add them to the results
  tableNames.forEach((tableName) => {
    if (tableName) {
      const regex = new RegExp(`\\b${tableName}\\b`);
      const standaloneRegex = new RegExp(`\\b${tableName}\\b(?!\\[)`, "g");

      if (regex.test(input)) {
        let match;
        while ((match = standaloneRegex.exec(input)) !== null) {
          namedRanges.push({ decoded: tableName, original: tableName, type: "nameRange" });
        }
      }
    }
  });

  return namedRanges;
};

export const extractNameRangesFromDecodedText = (text) => {
  // Regular expression pattern to match the name ranges
  const pattern = /\b(\w+(?:\.\w+)*(?:\.\.\w+(?:\.\w+)*)?)\b/g;
  const matches = text.match(pattern) || [];
  const validRanges = matches.filter((match) => match.includes(".") || match.includes(".."));

  return validRanges;
};

export const getExpressionValue = (name, dataArray) => {
  // TODO: If we have combined name Range return union of combind nameRanges

  // Find the object in the array that matches the given name
  const matchingObject = dataArray.find((item) => item.name.toLowerCase() === name.toLowerCase());

  // If no matching object is found, return null
  if (!matchingObject) {
    return "";
  }

  // Extract the expression
  const { expression } = matchingObject;

  // Find the index of '!' in the expression
  const exclamationIndex = expression.indexOf("!");

  // If '!' is not found, return null
  if (exclamationIndex === -1) {
    return expression;
  }

  // Return the substring after '!'
  return expression.slice(exclamationIndex + 1);
};

export const generateCombinations = (headerList) => {
  headerList = [...new Set(headerList)];
  const combinations = [];
  for (let i = 0; i < headerList.length - 1; i++) {
    for (let j = i + 1; j < headerList.length; j++) {
      combinations.push([String(headerList[i]).toLowerCase(), String(headerList[j]).toLowerCase()]);
    }
  }
  return combinations;
};
export const flattenNamedExpressions = (namedExpressionsMap) => {
  const flatNamedExpressions = [];

  Object.entries(namedExpressionsMap).forEach(([tableName, data]) => {
    if (data.type === "table") {
      // Handle table type - iterate through expressions array
      data.expressions.forEach((expr) => {
        flatNamedExpressions.push({
          name: expr.name,
          sheetId: data.sheet_id,
          expression: expr.expression,
          type: "table",
        });
      });
    } else {
      // Handle namedRange type - single expression
      flatNamedExpressions.push({
        name: tableName,
        localName: data.expressions[0].localName,
        sheetId: data.sheet_id,
        expression: data.expressions[0].expression,
        type: "namedRange",
        scope: data.scope,
        comment: data.comment,
      });
    }
  });

  return flatNamedExpressions;
};

export const groupNamedExpressions = (flatNamedExpressions) => {
  const groupedExpressions = {};

  flatNamedExpressions.forEach((expression) => {
    const [tableName] = expression.name.split(".", 1);

    if (expression.type === "table") {
      // Handle table type
      if (!groupedExpressions[tableName]) {
        groupedExpressions[tableName] = {
          sheet_id: expression.sheetId,
          type: "table",
          expressions: [],
        };
      }

      groupedExpressions[tableName].expressions.push({
        name: expression.name,
        expression: expression.expression,
      });
    } else {
      // Handle namedRange type
      groupedExpressions[tableName] = {
        sheet_id: expression.sheetId,
        type: "namedRange",
        scope: expression.scope,
        expression: expression.expression,
        comment: expression.comment,
      };
    }
  });

  return groupedExpressions;
};
export const findDollarParams = (input1, input2) => {
  // Helper function to clean and extract the core expression
  function extractCoreExpression(input) {
    // Remove the leading `=` sign and any formula name (e.g., "=SUM", "=AVG") and trim spaces
    return input
      .replace(/^=[^(]+\(?/, "")
      .replace(/\)?$/, "")
      .trim();
  }
  // If there's no parenthesis, remove only the leading '=' and formula text
  function removeEqualsAndFormula(input) {
    return input.replace(/^=/, "").trim();
  }

  // Handle cases where input contains parentheses or not
  const coreInput1 = input1.includes("(") ? extractCoreExpression(input1) : removeEqualsAndFormula(input1);
  const coreInput2 = input2.includes("(") ? extractCoreExpression(input2) : removeEqualsAndFormula(input2);

  // Split the expressions by operators or commas
  const params1 = coreInput1.split(/[\s,()+\-*/]+/).filter(Boolean);
  const params2 = coreInput2.split(/[\s,()+\-*/]+/).filter(Boolean);

  // Initialize result array and reference counter object
  let result = [];
  const referenceCounts = {};

  // Loop through params1, checking each for a dollar sign and comparing with params2
  for (let i = 0; i < params1.length; i++) {
    const param1 = params1[i];
    const param2 = params2[i];

    // Count each reference occurrence to handle indexing
    referenceCounts[param2] = (referenceCounts[param2] || 0) + 1;

    // Check if param1 contains a `$` sign and param2 does not
    if (param1.includes("$") && (!param2 || !param2.includes("$"))) {
      result.push({
        reference: param2,
        index: referenceCounts[param2] - 1,
      });
    }
  }

  return result;
};
export const toggleTableReferenceAbsRel = (reference) => {
  // Helper function to toggle $ sign
  const toggleDollar = (str) => (str.startsWith("$") ? str.slice(1) : "$" + str);

  // Patterns for different table reference formats
  const patterns = [
    {
      // TableName[Column] or TableName[$Column]
      regex: /^([A-Za-z0-9_]+\[)(\$?)([A-Za-z0-9_]+\d*)(\])$/,
      transform: (match) => `${match[1]}${toggleDollar(match[2] + match[3])}${match[4]}`,
    },
    {
      // TableName[[Column]:[Column]] or TableName[$[Column]:[Column]]
      regex: /^([A-Za-z0-9_]+\[)(\$?)(\[)([A-Za-z0-9_]+\d*)(\]:)(\[)([A-Za-z0-9_]+\d*)(\])(\])$/,
      transform: (match) =>
        `${match[1]}${toggleDollar(match[2])}${match[3]}${match[4]}${match[5]}${match[6]}${match[7]}${match[8]}${
          match[9]
        }`,
    },
    {
      // TableName[[#AnyHeader],[Column]] or TableName[[#AnyHeader],$[Column]]
      regex: /^([A-Za-z0-9_]+\[\[#[A-Za-z0-9_]+\],)(\$?)(\[)([A-Za-z0-9_]+\d*)(\])(\])$/,
      transform: (match) => `${match[1]}${toggleDollar(match[2])}${match[3]}${match[4]}${match[5]}${match[6]}`,
    },
    {
      // TableName[[#AnyHeader],[Column]:[Column]] or TableName[[#AnyHeader],$[Column]:[Column]]
      regex: /^([A-Za-z0-9_]+\[\[#[A-Za-z0-9_]+\],)(\$?)(\[)([A-Za-z0-9_]+\d*)(\]:)(\[)([A-Za-z0-9_]+\d*)(\])(\])$/,
      transform: (match) =>
        `${match[1]}${toggleDollar(match[2])}${match[3]}${match[4]}${match[5]}${match[6]}${match[7]}${match[8]}${
          match[9]
        }`,
    },
    {
      // Table[@$[Column1]:[Column3]] or Table[@[Column1]:[Column3]]
      regex: /^([A-Za-z0-9_]+\[@)(\$?)(\[)([A-Za-z0-9_]+\d*)(\]:)(\[)([A-Za-z0-9_]+\d*)(\])(\])$/,
      transform: (match) =>
        `${match[1]}${toggleDollar(match[2])}${match[3]}${match[4]}${match[5]}${match[6]}${match[7]}${match[8]}${
          match[9]
        }`,
    },
    {
      // Table[@$Column1] or Table[@Column1]
      regex: /^([A-Za-z0-9_]+\[@)(\$?)([A-Za-z0-9_]+\d*)(\])$/,
      transform: (match) => `${match[1]}${toggleDollar(match[2])}${match[3]}${match[4]}`,
    },
  ];

  // Try to match and transform the reference
  for (let pattern of patterns) {
    const match = reference.match(pattern.regex);
    if (match) {
      return pattern.transform(match);
    }
  }

  // If no match found, return the original reference
  return reference;
};

export const removePartUntilSymbol = (str, symbol) => {
  const index = str.indexOf(symbol);
  return index === -1 ? str : str.substring(index + 1);
};

export const findMatchingNameRange = (objectsArray, searchRange) => {
  // Helper function to remove $ from expressions
  function removeDollarSigns(expression) {
    return expression.replace(/\$/g, "");
  }

  // Remove $ from the searchRange
  const normalizedSearchRange = removeDollarSigns(searchRange);

  // Iterate through the array to find a matching range
  for (const obj of objectsArray) {
    // Remove $ from the expression in the object
    const normalizedExpression = removeDollarSigns(removePartUntilSymbol(obj.expression, "!"));

    // Check if the normalized expression matches the search range
    if (isRange(normalizedExpression) && normalizedExpression === normalizedSearchRange) {
      return obj.name;
    }
  }

  // If no match is found, return null or any other desired value
  return null;
};
// This function taking named expressions and merging them (default name will be added to it)
export const mergedNamedExpressions = (defaultNamedExpressions, newNamedExpressions, sheetId) => {
  const expressionMap = new Map();
  let mergedMap = [];

  // Populate expressionMap with arrays of names for each expression
  defaultNamedExpressions.forEach((entry) => {
    if (expressionMap.has(entry.expression)) {
      expressionMap.get(entry.expression).push(entry.name);
    } else {
      expressionMap.set(entry.expression, [entry.name]);
    }
  });

  newNamedExpressions.forEach((entry) => {
    const expression = entry.expression;
    const defaultNames = expressionMap.get(expression);

    if (expression) {
      // Find default name ending with "..data" or use the first name or null
      const defaultName =
        defaultNames && defaultNames.length > 0
          ? defaultNames.find((name) =>
              entry.name.endsWith("..data") ? name.endsWith("..data") : !name.endsWith("..data")
            )
          : null;
      mergedMap.push({
        name: entry.name,
        sheetId,
        defaultName,
        expression,
      });
    }
  });
  return mergedMap;
};

export const generateDefaultHeaders = (oldHeaders, defaultOldArray, newHeaders) => {
  // If lengths are different, find inserted or removed elements
  if (oldHeaders.length !== newHeaders.length) {
    return handleLengthDifference(oldHeaders, defaultOldArray, newHeaders);
  }

  // Check if all elements are in the same position
  const allSamePosition = oldHeaders.every((header, index) => header === newHeaders[index]);
  if (allSamePosition) {
    return defaultOldArray;
  }

  // Check if only updates happened without repositioning
  const onlyUpdates = oldHeaders.every(
    (header, index) =>
      header === newHeaders[index] || (header !== newHeaders[index] && !oldHeaders.includes(newHeaders[index]))
  );
  if (onlyUpdates) {
    return defaultOldArray;
  }

  // Check if only positions changed (no updates)
  const oldSet = new Set(oldHeaders);
  const newSet = new Set(newHeaders);
  if (oldSet.size === newSet.size && [...oldSet].every((value) => newSet.has(value))) {
    return newHeaders.map((header) => defaultOldArray[oldHeaders.indexOf(header)]);
  }

  // Handle updates and repositioning
  let maxColumnNumber = defaultOldArray.length - 1;
  const usedColumns = new Set(defaultOldArray);

  return newHeaders.map((header, index) => {
    if (header === oldHeaders[index]) {
      return defaultOldArray[index];
    } else {
      const oldIndex = oldHeaders.indexOf(header);
      if (oldIndex !== -1) {
        return defaultOldArray[oldIndex];
      } else {
        while (usedColumns.has(`Column${maxColumnNumber}`)) {
          maxColumnNumber++;
        }
        const newColumn = `Column${maxColumnNumber}`;
        usedColumns.add(newColumn);
        return newColumn;
      }
    }
  });
};
const handleLengthDifference = (oldHeaders, defaultOldArray, newHeaders) => {
  const result = [];
  let oldIndex = 0;
  let maxColumnNumber = Math.max(...defaultOldArray.map((col) => parseInt(col.replace(/\D/g, ""), 10) || 0));
  const usedColumns = new Set(defaultOldArray);

  for (let i = 0; i < newHeaders.length; i++) {
    if (oldIndex < oldHeaders.length && newHeaders[i] === oldHeaders[oldIndex]) {
      result.push(defaultOldArray[oldIndex]);
      oldIndex++;
    } else if (oldHeaders.includes(newHeaders[i])) {
      // Element moved
      const movedIndex = oldHeaders.indexOf(newHeaders[i]);
      result.push(defaultOldArray[movedIndex]);
    } else {
      // New element inserted
      maxColumnNumber++;
      const newColumn = `Column${maxColumnNumber}`;
      result.push(newColumn);
      usedColumns.add(newColumn);
    }
  }
  return result;
};

export const ensureMinimumRows = (range) => {
  const numberOfRows = range[3] - range[1];
  if (numberOfRows < 3) {
    range[3] = range[3] + (2 - numberOfRows);
  }
  return range;
};
